/**
 * Appearance: light | dark | system (follow OS dark/light).
 * Preference is stored in IndexedDB settings (themePreference) and mirrored to
 * localStorage for synchronous theme bootstrap before the app bundle loads.
 */

export const THEME_PREF_KEY_STORAGE = 'wiseentry-theme-preference';

export function isValidThemePreference(v) {
  return v === 'light' || v === 'dark' || v === 'system';
}

/**
 * Merge theme from IndexedDB settings row vs localStorage.
 *
 * IndexedDB holds the authoritative row from Settings → Save (when the write succeeds).
 * localStorage is mirrored on each save (before IDB) for the inline boot script.
 *
 * When the two disagree:
 * - IDB is explicit (light/dark) and LS is still "system" → prefer IDB (LS never synced or stale).
 * - IDB is "system" and LS is explicit → prefer LS (save flow wrote LS before put; IDB may have failed or be an old row).
 * - Both explicit and differ → prefer LS (last mirror from applyThemePreference right before put).
 *
 * @param {object | null | undefined} savedRow - Full settings object from IDB, or null
 * @returns {'light' | 'dark' | 'system'}
 */
export function resolveThemePreferenceForLoad(savedRow, mergedFallback) {
  const ls = getStoredPreference();
  const lsValid = isValidThemePreference(ls);
  const idbTheme =
    savedRow && typeof savedRow === 'object' && isValidThemePreference(savedRow.themePreference)
      ? savedRow.themePreference
      : null;

  if (idbTheme && lsValid && idbTheme !== ls) {
    if (ls === 'system' && idbTheme !== 'system') return idbTheme;
    if (idbTheme === 'system' && ls !== 'system') return ls;
    return ls;
  }
  if (idbTheme) return idbTheme;
  if (lsValid) return ls;
  if (isValidThemePreference(mergedFallback)) return mergedFallback;
  return 'system';
}

/** @returns {'light' | 'dark' | 'system'} */
export function getStoredPreference() {
  try {
    const v = localStorage.getItem(THEME_PREF_KEY_STORAGE);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch { /* ignore */ }
  return 'system';
}

/**
 * @param {'light' | 'dark' | 'system'} pref
 * @returns {'light' | 'dark'}
 */
export function resolveEffectiveTheme(pref) {
  if (pref === 'dark') return 'dark';
  if (pref === 'light') return 'light';
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}

/**
 * @param {'light' | 'dark'} effective
 */
export function applyEffectiveTheme(effective) {
  const root = document.documentElement;
  root.dataset.theme = effective;
  root.style.colorScheme = effective === 'dark' ? 'dark' : 'light';

  /** Single theme-color meta for taskbar/PWA tint */
  let meta = document.querySelector('meta[name="theme-color"][data-wiseentry-theme-meta]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', 'theme-color');
    meta.setAttribute('data-wiseentry-theme-meta', '1');
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', effective === 'dark' ? '#0a0f16' : '#0e1f3a');
}

/**
 * Mirror preference to localStorage and apply resolved theme.
 * @param {'light' | 'dark' | 'system'} pref
 */
export function applyThemePreference(pref) {
  try {
    localStorage.setItem(THEME_PREF_KEY_STORAGE, pref);
  } catch { /* ignore */ }
  applyEffectiveTheme(resolveEffectiveTheme(pref));
}

let systemListenerBound = false;

/**
 * Call after settings are loaded. Binds one global listener for OS theme changes when pref is "system".
 * @param {{ themePreference?: string } | null} settings
 */
export function initTheme(settings) {
  const pref =
    settings && isValidThemePreference(settings.themePreference)
      ? settings.themePreference
      : getStoredPreference();

  try {
    localStorage.setItem(THEME_PREF_KEY_STORAGE, pref);
  } catch { /* ignore */ }

  applyEffectiveTheme(resolveEffectiveTheme(pref));

  if (systemListenerBound) return;
  systemListenerBound = true;
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  mq.addEventListener('change', () => {
    const p = getStoredPreference();
    if (p === 'system') {
      applyEffectiveTheme(resolveEffectiveTheme('system'));
    }
  });
}
